'use client';

import * as React from 'react';

import { MarkdownPlugin } from '@platejs/markdown';
import { Plate, usePlateEditor } from 'platejs/react';

import { FieldFloatingToolbar } from '@/components/editor/field-floating-toolbar';
import { useArtifactCanvasHeaderActions } from '@/components/artifact/artifact-canvas-header-slot';
import { FieldEditorKit } from '@/components/editor/plugins/field-editor-kit';
import { DocumentEditorKit } from '@/components/editor/plugins/document-editor-kit';
import { ListViewSwitcher } from '@/components/filters/list-view-switcher';
import {
  RICH_TEXT_FOCUSED_PLACEHOLDER,
  RICH_TEXT_MARKDOWN_PLACEHOLDER,
} from '@/lib/rich-text-empty-state';
import { RICH_TEXT_MODE_OPTIONS } from '@/lib/rich-text-mode-options';
import { cn } from '@/lib/utils';
import { Editor, EditorContainer } from '@/components/ui/editor';
import { Textarea } from '@/components/ui/textarea';

import {
  clampRichTextChange,
  RICH_TEXT_CANVAS_PADDING_CLASS,
  RICH_TEXT_VISUAL_SERIALIZE_DEBOUNCE_MS,
  resolveRichTextModeSwitcher,
  shouldApplyExternalRichTextValue,
  type RichTextChrome,
  type RichTextEditorMode,
  type RichTextFrame,
  type RichTextInputProps,
} from './rich-text-shared';
import { humanizeRichTextMarkdown } from '@/lib/markdown-field';
import {
  richTextVisibleCharCount,
  richTextVisibleCharCountFromNodes,
} from '@/lib/rich-text-visible-char-count';
import type { Descendant } from 'platejs';

const CHROME_LAYOUT: Record<
  RichTextChrome,
  {
    previewMinHeight: string;
    markdownMinHeight: string;
    markdownRows: number;
    plainTextareaRows: number;
  }
> = {
  field: {
    previewMinHeight: 'min-h-32',
    markdownMinHeight: 'min-h-32',
    markdownRows: 8,
    plainTextareaRows: 5,
  },
  document: {
    previewMinHeight: 'min-h-48',
    markdownMinHeight: 'min-h-48',
    markdownRows: 14,
    plainTextareaRows: 12,
  },
};

export type RichTextPlateShellProps = RichTextInputProps & {
  chrome: RichTextChrome;
  defaultMode?: RichTextEditorMode;
};

export function RichTextPlateShell({
  id,
  value,
  onChange,
  placeholder,
  disabled = false,
  maxLength,
  'aria-invalid': ariaInvalid,
  className,
  chrome,
  frame = 'field',
  mode: controlledMode,
  onModeChange,
  modeSwitcher,
  modeSwitcherLeading,
  canvasContentClassName,
  defaultMode = 'preview',
  onCharCountChange,
}: RichTextPlateShellProps) {
  const layout = CHROME_LAYOUT[chrome];
  const isCanvas = frame === 'canvas';
  const editorPlugins = chrome === 'document' ? DocumentEditorKit : FieldEditorKit;
  const resolvedModeSwitcher = resolveRichTextModeSwitcher(frame, modeSwitcher);
  const showInlineSwitcher = resolvedModeSwitcher === 'inline';
  const showHeaderSwitcher = resolvedModeSwitcher === 'header';

  const [uncontrolledMode, setUncontrolledMode] = React.useState<RichTextEditorMode>(defaultMode);
  const mode = controlledMode ?? uncontrolledMode;
  const setMode = React.useCallback(
    (next: RichTextEditorMode) => {
      if (controlledMode === undefined) {
        setUncontrolledMode(next);
      }
      onModeChange?.(next);
    },
    [controlledMode, onModeChange],
  );

  const [markdownDraft, setMarkdownDraft] = React.useState(() => humanizeRichTextMarkdown(value));
  const [visibleCharCount, setVisibleCharCount] = React.useState(() =>
    maxLength != null ? richTextVisibleCharCount(value) : 0,
  );
  const serializeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedValueRef = React.useRef(value);

  const editor = usePlateEditor({
    id,
    plugins: editorPlugins,
    value: (ed) => ed.getApi(MarkdownPlugin).markdown.deserialize(value || ''),
  });

  React.useEffect(() => {
    if (mode !== 'markdown') return;
    setMarkdownDraft((prev) => {
      const next = humanizeRichTextMarkdown(value);
      return prev === next ? prev : next;
    });
  }, [value, mode]);

  React.useEffect(
    () => () => {
      if (serializeTimerRef.current) clearTimeout(serializeTimerRef.current);
    },
    [],
  );

  const commitMarkdown = React.useCallback(
    (next: string) => {
      lastEmittedValueRef.current = next;
      clampRichTextChange(next, maxLength, onChange);
    },
    [maxLength, onChange],
  );

  const countVisible = React.useCallback(
    (nextMarkdown?: string, nextNodes?: Descendant[]) => {
      if (nextNodes) return richTextVisibleCharCountFromNodes(nextNodes);
      const markdownSource =
        nextMarkdown ?? (mode === 'markdown' ? markdownDraft : undefined);
      if (markdownSource !== undefined) {
        return richTextVisibleCharCountFromNodes(
          editor.api.markdown.deserialize(humanizeRichTextMarkdown(markdownSource)),
        );
      }
      return richTextVisibleCharCountFromNodes(editor.children);
    },
    [editor, markdownDraft, mode],
  );

  const emitCharCount = React.useCallback(
    (nextMarkdown?: string, nextNodes?: Descendant[]) => {
      if (maxLength == null) return;
      const count = countVisible(nextMarkdown, nextNodes);
      setVisibleCharCount(count);
      onCharCountChange?.(count);
    },
    [countVisible, maxLength, onCharCountChange],
  );

  React.useEffect(() => {
    emitCharCount();
  }, [emitCharCount, value]);

  React.useEffect(() => {
    if (!shouldApplyExternalRichTextValue(value, lastEmittedValueRef.current)) return;
    lastEmittedValueRef.current = value;
    const next = value || '';
    editor.tf.reset();
    editor.tf.setValue(editor.api.markdown.deserialize(next));
    setMarkdownDraft(humanizeRichTextMarkdown(next));
    emitCharCount(next);
  }, [editor, emitCharCount, value]);

  const applyModeTransition = React.useCallback(
    (next: RichTextEditorMode, previous: RichTextEditorMode) => {
      if (next === previous) return;

      if (serializeTimerRef.current) {
        clearTimeout(serializeTimerRef.current);
        serializeTimerRef.current = null;
      }

      if (next === 'markdown') {
        const serialized = humanizeRichTextMarkdown(editor.api.markdown.serialize());
        setMarkdownDraft(serialized);
        commitMarkdown(serialized);
        emitCharCount(serialized);
        return;
      }

      if (previous === 'markdown') {
        editor.tf.reset();
        editor.tf.setValue(editor.api.markdown.deserialize(markdownDraft || ''));
        commitMarkdown(markdownDraft);
        emitCharCount(markdownDraft);
      }
    },
    [commitMarkdown, editor, emitCharCount, markdownDraft],
  );

  const previousModeRef = React.useRef(mode);
  const skipControlledModeEffectRef = React.useRef(true);
  React.useEffect(() => {
    if (controlledMode === undefined) return;
    if (skipControlledModeEffectRef.current) {
      skipControlledModeEffectRef.current = false;
      previousModeRef.current = mode;
      return;
    }
    applyModeTransition(mode, previousModeRef.current);
    previousModeRef.current = mode;
  }, [applyModeTransition, controlledMode, mode]);

  const switchMode = React.useCallback(
    (next: RichTextEditorMode) => {
      if (next === mode) return;
      applyModeTransition(next, mode);
      setMode(next);
      previousModeRef.current = next;
    },
    [applyModeTransition, mode, setMode],
  );

  const handlePreviewChange = React.useCallback(
    ({ value: nextValue }: { editor: typeof editor; value: Descendant[] }) => {
      emitCharCount(undefined, nextValue);
      if (serializeTimerRef.current) clearTimeout(serializeTimerRef.current);
      serializeTimerRef.current = setTimeout(() => {
        commitMarkdown(humanizeRichTextMarkdown(editor.api.markdown.serialize()));
      }, RICH_TEXT_VISUAL_SERIALIZE_DEBOUNCE_MS);
    },
    [commitMarkdown, editor, emitCharCount],
  );

  const modeSwitcherControl = (
    <ListViewSwitcher
      value={mode}
      onValueChange={(next) => switchMode(next as RichTextEditorMode)}
      options={RICH_TEXT_MODE_OPTIONS}
      ariaLabel="Editor mode"
      variant="icon"
    />
  );

  useArtifactCanvasHeaderActions(showHeaderSwitcher ? modeSwitcherControl : null);

  const charCount =
    maxLength != null
      ? { current: visibleCharCount, max: maxLength }
      : undefined;

  const previewPlaceholder = placeholder ?? RICH_TEXT_FOCUSED_PLACEHOLDER;

  const markdownPlaceholder = placeholder ?? RICH_TEXT_MARKDOWN_PLACEHOLDER;

  const editorVariant = isCanvas ? 'canvas' : 'field';
  const previewEditorClass = isCanvas
    ? 'min-h-0 flex-1'
    : layout.previewMinHeight;
  const markdownEditorClass = isCanvas
    ? 'min-h-0 flex-1 resize-none'
    : cn(layout.markdownMinHeight, 'resize-y');

  const editorSurface = (
    <>
      <Plate
        editor={editor}
        onValueChange={mode === 'preview' ? handlePreviewChange : undefined}
      >
        <div
          className={cn(
            'flex min-h-0 flex-col',
            isCanvas ? 'flex-1' : undefined,
            mode === 'markdown' && 'hidden',
          )}
        >
          <EditorContainer
            variant={isCanvas ? 'canvas' : 'field'}
            className={cn(isCanvas ? 'min-h-0 flex-1 overflow-y-auto' : previewEditorClass)}
          >
            <Editor
              id={mode === 'preview' ? id : undefined}
              variant={editorVariant}
              placeholder={previewPlaceholder}
              disabled={disabled}
              aria-invalid={ariaInvalid}
            />
            {mode === 'preview' ? <FieldFloatingToolbar /> : null}
          </EditorContainer>
        </div>

        {mode === 'markdown' ? (
          <Textarea
            id={id}
            value={markdownDraft}
            onChange={(event) => {
              const next = event.target.value;
              setMarkdownDraft(next);
              emitCharCount(next);
              commitMarkdown(next);
            }}
            placeholder={markdownPlaceholder}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            className={cn(
              markdownEditorClass,
              'rounded-none border-0 font-mono text-sm shadow-none focus-visible:ring-0',
              // Canvas already pads via RICH_TEXT_CANVAS_PADDING_CLASS — zero out Textarea defaults
              // so Markdown text lines up with Preview (and the create-job title above).
              isCanvas && 'min-h-0 flex-1 px-0 py-0',
              !isCanvas && 'px-3',
            )}
            rows={isCanvas ? undefined : layout.markdownRows}
          />
        ) : null}
      </Plate>

      {charCount && !isCanvas ? (
        <p
          className="shrink-0 px-3 py-1.5 text-right text-xs text-muted-foreground tabular-nums"
          aria-live="polite"
        >
          {charCount.current} / {charCount.max}
        </p>
      ) : null}
    </>
  );

  return (
    <div
      className={cn(
        isCanvas
          ? 'flex min-h-0 flex-1 flex-col bg-transparent'
          : cn(
              'rounded-md border border-input bg-transparent shadow-xs',
              'focus-within:ring-[3px] focus-within:ring-ring/50',
              ariaInvalid && 'border-destructive focus-within:ring-destructive/20',
            ),
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {showInlineSwitcher ? (
        <div
          className={cn(
            'flex shrink-0 items-center gap-3',
            modeSwitcherLeading ? 'justify-between' : 'justify-end',
            // Field chrome: bordered toolbar bar. Canvas inline: title + switcher, no divider.
            isCanvas
              ? 'pb-2'
              : 'border-b border-border bg-muted/30 p-1',
          )}
        >
          {modeSwitcherLeading ? (
            <div className="min-w-0 flex-1">{modeSwitcherLeading}</div>
          ) : null}
          {modeSwitcherControl}
        </div>
      ) : null}

      {isCanvas ? (
        <div
          className={cn(
            RICH_TEXT_CANVAS_PADDING_CLASS,
            canvasContentClassName,
            'flex min-h-0 flex-1 flex-col overflow-hidden min-h-48',
          )}
        >
          {editorSurface}
        </div>
      ) : (
        <div className="flex min-h-0 flex-col">{editorSurface}</div>
      )}
    </div>
  );
}

export function RichTextPlainFallback({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  maxLength,
  'aria-invalid': ariaInvalid,
  className,
  chrome,
  frame = 'field',
}: RichTextInputProps & { chrome: RichTextChrome; frame?: RichTextFrame }) {
  const rows = CHROME_LAYOUT[chrome].plainTextareaRows;
  const isCanvas = frame === 'canvas';

  return (
    <Textarea
      id={id}
      value={value}
      onChange={(event) => clampRichTextChange(event.target.value, maxLength, onChange)}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={ariaInvalid}
      className={cn(
        isCanvas &&
          cn(
            'min-h-0 flex-1 resize-none border-0 shadow-none',
            RICH_TEXT_CANVAS_PADDING_CLASS,
          ),
        className,
      )}
      rows={isCanvas ? undefined : rows}
    />
  );
}
